import { Text } from "@solomon-labs/design/components/ui";
import * as React from "react";

import { cn } from "../../utils/cn";

import { Button } from "./button";

/**
 * Solomon Accordion — built the house way: a `Button variant="accordion"`
 * trigger + a Group organizer for state, mirroring `ToggleGroup` /
 * `radio-group`. No Base UI. The expand/collapse reveal and the ember dot
 * indicator are lifted directly from the live `StepAccordion` reference
 * (grid-rows `1fr`↔`0fr` with an inner `overflow-hidden` clip).
 *
 *   - `AccordionGroup`   — the organizer. Owns the open set + toggle policy
 *                          (single/multiple, collapsible), plus the shared
 *                          marker/indicator style. Controlled via
 *                          `value`/`onValueChange`, or uncontrolled via
 *                          `defaultValue`.
 *   - `AccordionItem`    — one row. Carries its `value`, disabled state,
 *                          stable trigger/panel ids, and 1-based number.
 *   - `AccordionTrigger` — the row header. Renders the Button; lays out
 *                          [leading marker][title][trailing indicator] and
 *                          wires ARIA + the toggle handler.
 *   - `AccordionPanel`   — the collapsible content region.
 */

type AccordionType = "single" | "multiple";
type AccordionMarker = "none" | "number" | "icon";
type AccordionIndicator = "dot" | "chevron" | "none";

/* ── Group context ──────────────────────────────────────────────────── */

interface AccordionGroupContextValue {
    openValues: ReadonlySet<string>;
    toggle: (value: string) => void;
    type: AccordionType;
    collapsible: boolean;
    marker: AccordionMarker;
    indicator: AccordionIndicator;
    /** Registers an item and returns its stable 1-based number. Registration
     *  order follows DOM/render order, so the number tracks the row's
     *  position for the `number` marker. */
    register: (value: string) => number;
}

const AccordionGroupContext =
    React.createContext<AccordionGroupContextValue | null>(null);

function useAccordionGroup(): AccordionGroupContextValue {
    const ctx = React.useContext(AccordionGroupContext);
    if (!ctx) {
        throw new Error(
            "AccordionItem/AccordionTrigger/AccordionPanel must be used within an AccordionGroup"
        );
    }
    return ctx;
}

/* ── Item context ───────────────────────────────────────────────────── */

interface AccordionItemContextValue {
    value: string;
    isOpen: boolean;
    disabled: boolean;
    triggerId: string;
    panelId: string;
    /** 1-based position, for the `number` marker. */
    number: number;
}

const AccordionItemContext =
    React.createContext<AccordionItemContextValue | null>(null);

function useAccordionItem(): AccordionItemContextValue {
    const ctx = React.useContext(AccordionItemContext);
    if (!ctx) {
        throw new Error(
            "AccordionTrigger/AccordionPanel must be used within an AccordionItem"
        );
    }
    return ctx;
}

/* ── AccordionGroup ─────────────────────────────────────────────────── */

interface AccordionGroupProps {
    type?: AccordionType;
    /** Controlled open value(s). `string` for `single`, `string[]` for
     *  `multiple`. Pair with `onValueChange`. */
    value?: string | string[];
    /** Uncontrolled initial open value(s). */
    defaultValue?: string | string[];
    onValueChange?: (value: string | string[]) => void;
    /** Single-select only: whether the open item can be toggled closed.
     *  No-op for `multiple` (items always toggle independently). */
    collapsible?: boolean;
    marker?: AccordionMarker;
    /** House default is the ember square dot. */
    indicator?: AccordionIndicator;
    className?: string;
    children?: React.ReactNode;
}

function toSet(
    value: string | string[] | undefined,
    type: AccordionType
): Set<string> {
    if (value === null || value === undefined) return new Set();
    if (Array.isArray(value)) return new Set(value);
    // single: a bare string is one open item; ignore arrays mismatch by
    // wrapping. (type=single always stores at most one.)
    return type === "single" ? new Set([value]) : new Set([value]);
}

function AccordionGroup({
    type = "single",
    value,
    defaultValue,
    onValueChange,
    collapsible = false,
    marker = "none",
    indicator = "dot",
    className,
    children
}: AccordionGroupProps): React.ReactElement {
    const isControlled = value !== undefined;

    const [internalOpen, setInternalOpen] = React.useState<Set<string>>(() =>
        toSet(defaultValue, type)
    );

    const openValues = isControlled ? toSet(value, type) : internalOpen;

    // Item registration → stable 1-based numbering in render order. A ref
    // map keeps assigned numbers stable across re-renders; a render-pass
    // counter (reset each render) drives the order. React renders children
    // top-to-bottom, so first-seen value gets 1, next gets 2, etc.
    const numbersRef = React.useRef<Map<string, number>>(new Map());
    const counterRef = React.useRef(0);
    counterRef.current = 0;

    const register = React.useCallback((itemValue: string): number => {
        counterRef.current += 1;
        const next = counterRef.current;
        numbersRef.current.set(itemValue, next);
        return next;
    }, []);

    const emit = React.useCallback(
        (nextSet: Set<string>) => {
            if (!isControlled) setInternalOpen(nextSet);
            if (onValueChange) {
                const next =
                    type === "single"
                        ? (Array.from(nextSet)[0] ?? "")
                        : Array.from(nextSet);
                onValueChange(next);
            }
        },
        [isControlled, onValueChange, type]
    );

    const toggle = React.useCallback(
        (itemValue: string) => {
            const current = isControlled ? toSet(value, type) : internalOpen;
            const isOpen = current.has(itemValue);

            if (type === "multiple") {
                const next = new Set(current);
                if (isOpen) next.delete(itemValue);
                else next.add(itemValue);
                emit(next);
                return;
            }

            // single
            if (isOpen) {
                // Clicking the open row only closes it when collapsible.
                if (!collapsible) return;
                emit(new Set());
                return;
            }
            emit(new Set([itemValue]));
        },
        [collapsible, emit, internalOpen, isControlled, type, value]
    );

    const ctx = React.useMemo<AccordionGroupContextValue>(
        () => ({
            openValues,
            toggle,
            type,
            collapsible,
            marker,
            indicator,
            register
        }),
        [openValues, toggle, type, collapsible, marker, indicator, register]
    );

    return (
        <AccordionGroupContext.Provider value={ctx}>
            <div className={cn("flex w-full flex-col", className)}>
                {children}
            </div>
        </AccordionGroupContext.Provider>
    );
}

/* ── AccordionItem ──────────────────────────────────────────────────── */

interface AccordionItemProps {
    value: string;
    disabled?: boolean;
    className?: string;
    children?: React.ReactNode;
}

function AccordionItem({
    value,
    disabled = false,
    className,
    children
}: AccordionItemProps): React.ReactElement {
    const group = useAccordionGroup();
    const reactId = React.useId();

    // Register for ordered numbering. Runs each render (counter resets in
    // the group per render pass), keeping the number aligned with order.
    const number = group.register(value);

    const isOpen = group.openValues.has(value);
    const triggerId = `${reactId}-trigger`;
    const panelId = `${reactId}-panel`;

    const itemCtx = React.useMemo<AccordionItemContextValue>(
        () => ({ value, isOpen, disabled, triggerId, panelId, number }),
        [value, isOpen, disabled, triggerId, panelId, number]
    );

    return (
        <AccordionItemContext.Provider value={itemCtx}>
            <div
                data-slot="accordion-item"
                data-state={isOpen ? "open" : "closed"}
                className={cn("not-last:border-b border-line-base", className)}
            >
                {children}
            </div>
        </AccordionItemContext.Provider>
    );
}

/* ── AccordionTrigger ───────────────────────────────────────────────── */

interface AccordionTriggerProps {
    /** Override the leading marker node entirely. */
    marker?: React.ReactNode;
    /** Leading node when the group marker is `icon`. */
    icon?: React.ReactNode;
    className?: string;
    children?: React.ReactNode;
}

function AccordionTrigger({
    marker: markerOverride,
    icon,
    className,
    children
}: AccordionTriggerProps): React.ReactElement {
    const group = useAccordionGroup();
    const item = useAccordionItem();

    const leading = resolveMarker(
        markerOverride,
        group.marker,
        icon,
        item.number
    );
    const trailing = resolveIndicator(group.indicator, item.isOpen);

    return (
        <Button
            id={item.triggerId}
            variant="accordion"
            interaction="default"
            size="md"
            disabled={item.disabled}
            aria-expanded={item.isOpen}
            aria-controls={item.panelId}
            onClick={() => group.toggle(item.value)}
            // `px-2` is the inner padding that keeps the content off the row
            // edge (the hover fill itself spans the full row) — overrides the
            // `size` preset's px so the padding doesn't drift with size. `py-4`
            // sets row height.
            className={cn("px-2 py-4", className)}
        >
            {/* Marker + title grouped on the left; the indicator is the right
                sibling, so the Button's `justify-between` pushes it to the row
                edge (no `flex-1` reliance — a marker + per-trigger gap could
                otherwise leave the indicator stranded mid-row). */}
            <span className="flex min-w-0 flex-1 items-center gap-3">
                {leading}
                <span className="min-w-0 text-left">{children}</span>
            </span>
            {trailing}
        </Button>
    );
}

function resolveMarker(
    override: React.ReactNode,
    marker: AccordionMarker,
    icon: React.ReactNode,
    number: number
): React.ReactNode {
    if (override !== undefined) return override;
    if (marker === "number") {
        return (
            <Text
                variant="number"
                render={<span />}
                aria-hidden
                className="shrink-0 w-5 tabular-nums text-foreground-neutral"
            >
                {number}
            </Text>
        );
    }
    if (marker === "icon" && icon !== null && icon !== undefined) {
        return <span className="shrink-0">{icon}</span>;
    }
    return null;
}

function resolveIndicator(
    indicator: AccordionIndicator,
    isOpen: boolean
): React.ReactNode {
    if (indicator === "none") return null;
    if (indicator === "dot") {
        // The one earned ember accent on the row — mirrors StepAccordion.
        return (
            <span
                aria-hidden
                className={cn(
                    "size-1.5 shrink-0 transition-colors duration-200",
                    isOpen ? "bg-ember" : "bg-foreground-imprint"
                )}
            />
        );
    }
    // chevron — a simple down-chevron that flips on open.
    return (
        <svg
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className={cn(
                "size-4 shrink-0 text-foreground-neutral transition-transform duration-200",
                isOpen && "rotate-180"
            )}
        >
            <path
                d="M4 6l4 4 4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/* ── AccordionPanel ─────────────────────────────────────────────────── */

interface AccordionPanelProps {
    className?: string;
    children?: React.ReactNode;
}

function AccordionPanel({
    className,
    children
}: AccordionPanelProps): React.ReactElement {
    const item = useAccordionItem();

    return (
        <div
            id={item.panelId}
            role="region"
            aria-labelledby={item.triggerId}
            data-slot="accordion-panel"
            data-state={item.isOpen ? "open" : "closed"}
            className={cn(
                // Grid-rows reveal, lifted verbatim from StepAccordion: the
                // outer grid animates its single row track between 1fr and
                // 0fr; the inner wrapper clips the overflow during collapse.
                "grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none",
                item.isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            )}
        >
            <div className="overflow-hidden">
                <div className={cn("pb-4", className)}>{children}</div>
            </div>
        </div>
    );
}

export { AccordionGroup, AccordionItem, AccordionTrigger, AccordionPanel };
export type {
    AccordionGroupProps,
    AccordionItemProps,
    AccordionTriggerProps,
    AccordionPanelProps,
    AccordionType,
    AccordionMarker,
    AccordionIndicator
};
